import { Injectable } from '@angular/core';
import { ImagesService } from './images.service';
import { MediaType } from 'src/app/shared-form-sections/types/media-type';
import { MediaTypes } from 'src/app/shared-form-sections/enums/media-types';
import { map } from 'rxjs/operators';
import { VideosService } from './videos.service';
import { IMedia } from 'src/app/core/models/i-media';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { Observable } from 'rxjs';
import { ISlide, Slide } from 'src/app/core/dtos/interfaces/i-slide';

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  constructor(
    private imageService: ImagesService,
    private videoService: VideosService
  ) {}

  getFiltered$(
    mediaType: MediaType,
    {
      currentPage,
      currentPageSize,
    }: { currentPage: number; currentPageSize: number } = {
      currentPage: 1,
      currentPageSize: 5,
    },
    {
      filters = {},
    }: {
      filters?: { [key: string]: unknown };
    } = {
      filters: {},
    }
  ): Observable<{ results: IMedia[]; totalCount: number }> {
    if (mediaType === MediaTypes.IMAGE) {
      return this.imageService
        .getFiltered$(
          { currentPage, currentPageSize },
          {
            filters,
          }
        )
        .pipe(
          map((r) => ({
            results: r.results.map((result) => this.mapImageToMedia(result)),
            totalCount: r.totalCount,
          }))
        );
    }
    if (mediaType === MediaTypes.VIDEO) {
      return this.videoService
        .getFiltered$(
          { currentPage, currentPageSize },
          {
            filters,
          }
        )
        .pipe(
          map((response) => ({
            results: response.results.map((result) =>
              this.mapVideoToMedia(result)
            ),
            totalCount: response.totalCount,
          }))
        );
    }
  }

  mapToImage(media: IMedia): IImgMediaDto {
    return {
      id: media.id,
      name: media.name,
      inlineUrl: media.inlineUrl,
      uploadedDate: media.uploadedDate,
      imageUrl: media.inlineUrl,
      thumbnailUrl: media.thumbnailUrl,
      imageIdentifier: media.id,
      inlineImageUrl: media.inlineUrl,
      caption: media.caption,
      canUseInArticle: media.canUseInArticle,
    };
  }

  mapSlideToImage(slide: ISlide): IImgMediaDto {
    return {
      id: slide.mediaId,
      name: slide.mediaTitle,
      thumbnailUrl: slide.mediaThumbnailUrl,
      imageIdentifier: slide.mediaId,
      caption: slide.caption,
    };
  }

  mapImageToSlide(img: IImgMediaDto, id: number, order: number): ISlide {
    if (img) {
      return {
        id: id,
        order: order,
        mediaId: img.id,
        mediaTitle: img.name,
        mediaThumbnailUrl: img.thumbnailUrl,
        caption: img.caption,
      };
    } else return new Slide(id, order);
  }

  mapToVideo(media: IMedia): IVideoMediaDto {
    return {
      thumbnailUrl: media.thumbnailUrl,
      guid: media.id,
      title: media.name,
      createdDate: media.uploadedDate,
    };
  }

  mapVideoToMedia(video: IVideoMediaDto): IMedia {
    return {
      id: video.guid.toString(),
      name: video.title,
      inlineUrl: video.thumbnailUrl,
      thumbnailUrl: video.thumbnailUrl,
      uploadedDate: video.createdDate,
      canUseInArticle: true,
    };
  }

  mapImageToMedia(image: IImgMediaDto): IMedia {
    return {
      id: image.imageIdentifier,
      name: image.name,
      inlineUrl: image.inlineUrl ?? image.imageUrl,
      thumbnailUrl: image.thumbnailUrl ?? image.imageUrl,
      uploadedDate: image.uploadedDateUtc,
      caption: image.caption,
      canUseInArticle: image.canUseInArticle,
    };
  }
}
